import { html, LitElement } from 'lit';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { customElement, property, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { styles } from '../view/styles';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { InputWidthValues } from '../../../../../../../../ui/gzInput';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { GzCheckboxWebComponent } from '../../../../../../../../ui/gzCheckbox';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { MAX_PORT_VALUE, MIN_PORT_VALUE } from '../../../../../utils/uiSettings/sandbox/sandbox.values';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { TextHighlightMixin } from '../../../../../../../../mixins';
import { selectAccordionsSearchState } from '@nirvana/redux/searchable-ui-accordions';
import { FormEvent } from '../../../../../../../../ui/gzForm/formField/types/form-event.enum';
import { Subscription } from 'rxjs';

@customElement('gz-policy-panel-sandbox-proxy-configuration')
export class GzProxyConfigurationWebComponent extends TextHighlightMixin(connect(store)(LitElement)) {
  @property({ type: Object, attribute: true, reflect: true })
  public proxyConfigurationLabel: { label: string; translatedLabel: string } = {
    label: '',
    translatedLabel: '',
  };

  @query('#use-proxy-form-field')
  public useProxyFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#proxy-configuration-form-fields')
  public proxyConfigurationFieldsSection: HTMLElement;
  @query('#server-form-field')
  public serverFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#port-form-field')
  public portFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#username-form-field')
  public usernameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#password-form-field')
  public passwordFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  public formGroup: FormGroup;
  private formGroupValidSubscription: Subscription;

  /**
   * State changed, update search string.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const accordionsSearchApp = selectAccordionsSearchState(rootState);

    this.searchString = accordionsSearchApp.searchString;
  }

  /**
   * Template styles.
   */
  static get styles() {
    return styles;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);

    this.usernameFormField.touched = true;

    this.buildFormGroup();
    this.setValidators();
    this.subscribeToValidityEvent();
  }

  protected render() {
    return html`
      <gz-form-field class="label-mw-250" id="use-proxy-form-field">
        <gz-checkbox
          searchable-label="${this.proxyConfigurationLabel.label}"
          label="${this.proxyConfigurationLabel.translatedLabel}"
          gz-form-input
          @change="${this.handleUseProxyChange.bind(this)}"
        ></gz-checkbox>
        <gz-text fontSize="12" fontWeight="400" class="form-field-info-text"
          >${translateFn('Connect the endpoint sensor and the Sandbox Analyzer portal through a proxy server.')}
        </gz-text>
      </gz-form-field>

      <div id="proxy-configuration-form-fields" class="hidden">
        <gz-form-field id="server-form-field" class="label-mw-250" label="${translateFn('Server')}" required>
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Enter server address')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="port-form-field" class="label-mw-250" label="${translateFn('Port')}" required>
          <gz-input-number
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Enter port')}"
            min="${MIN_PORT_VALUE}"
            max="${MAX_PORT_VALUE}"
          ></gz-input-number>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="username-form-field" class="label-mw-250" label="${translateFn('Username')}">
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Enter username')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="password-form-field" class="label-mw-250" label="${translateFn('Password')}">
          <gz-input
            gz-form-input
            type="password"
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Enter password')}"
            @change="${e => {
              // add required validator to username field if password is not empty
              this.usernameFormField.Validators = e.detail.sourceEvent
                ? [RequiredValidator(translateFn('Required field'))]
                : [];
            }}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private buildFormGroup() {
    this.formGroup = FormGroup.build({
      enabled: this.useProxyFormField,
      settings: {
        server: this.serverFormField,
        port: this.portFormField,
        username: this.usernameFormField,
        password: this.passwordFormField,
      },
    });
  }

  private setValidators() {
    this.serverFormField.addEventListener(
      FormEvent.ValueChange,
      () => (this.serverFormField.Validators = [RequiredValidator(translateFn('Required field'))]),
      {
        once: true,
      }
    );
    this.portFormField.addEventListener(
      FormEvent.ValueChange,
      () => (this.portFormField.Validators = [RequiredValidator(translateFn('Required field'))]),
      {
        once: true,
      }
    );
  }

  protected subscribeToValidityEvent(): void {
    // subscribe to form group validity changes
    this.formGroupValidSubscription = this.formGroup.valid$.subscribe({
      next: _ => {
        this.handleUseProxyChange();
      },
    });
  }

  private handleUseProxyChange() {
    const isChecked = this.useProxyFormField.value;
    const isValid = this.formGroup.valid;
    toggleVisibility(this.proxyConfigurationFieldsSection, isChecked || !isValid);
    toggleDisabled(this.proxyConfigurationFieldsSection, !isChecked && !isValid);
  }

  public disconnectedCallback(): void {
    super.disconnectedCallback();
    this.formGroupValidSubscription.unsubscribe();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-policy-panel-sandbox-proxy-configuration': GzProxyConfigurationWebComponent;
  }
}
